/**
 * GO Commerce Cacheable Backend Service
 * 
 * Wraps existing backend services with transparent caching to improve performance
 * and reduce API calls. Automatically handles cache invalidation and refresh.
 */

import { apiCache } from '@/utils/apiCache';
import { 
  useAuthService,
  useUsageService,
  useIntegrateService,
  useSimService,
  useVoucherService,
  useMobilePlanService
} from './';
import { ref } from 'vue';
import { sanitizeResponse, sanitizeCookie } from '@/utils/sanitizeResponse';

// Add type declaration for window property
declare global {
  interface Window {
    __goCacheLastSaved?: {
      key: string;
      timestamp: number;
    };
    __networkBalanceLastCalled?: {
      timestamp: number;
      customerId: string;
    };
  }
}

// Cache time configurations - each endpoint has a specific TTL
const CACHE_TIMES = {
  PRODUCTS: 60 * 60 * 1000,          // 1 hour for product catalog
  NETWORK_BALANCE: 5 * 60 * 1000,    // 5 minutes for usage data
  CURRENT_SERVICES: 15 * 60 * 1000,  // 15 minutes for services
  USER_PROFILE: 30 * 60 * 1000,      // 30 minutes for user profile
  RECHARGES: 10 * 60 * 1000          // 10 minutes for recharges
};

// Flag to detect server-side rendering
const isSSR = typeof window === 'undefined';

// In-memory cache for auth requests to prevent redundant calls
// Added May 2025: Fixes the multiple redundant calls to /api/custom-auth/me
let sessionRequestInProgress = false;
let sessionPromiseCache: Promise<any> | null = null;
let lastSessionTimestamp = 0;
const MEMORY_CACHE_TTL = 2000; // 2 seconds in-memory cache

export const useCacheableBackendService = () => {
  // Flag to track if we're in server-side rendering
  const isServerSide = ref(isSSR);
  
  // Get original service instances
  const authService = useAuthService();
  const usageService = useUsageService();
  const integrateService = useIntegrateService();
  const simService = useSimService();
  const voucherService = useVoucherService();
  const mobilePlanService = useMobilePlanService();
  
  // Session/User Profile - wrapped with caching
  const getSession = async (forceFresh = false) => {
    // Added May 2025: Short-term memory cache to prevent duplicate API calls during component mounting
    const now = Date.now();
    
    // If another request is already in progress, wait for it instead of making a new one
    if (sessionRequestInProgress && sessionPromiseCache) {
      console.log('[CacheableBackend] Session request already in progress, waiting for result');
      return await sessionPromiseCache;
    }
    
    // For non-forced requests, check if we've fetched the session recently
    if (!forceFresh && lastSessionTimestamp > 0 && (now - lastSessionTimestamp < MEMORY_CACHE_TTL)) {
      console.log(`[CacheableBackend] Using in-memory session data (${(now - lastSessionTimestamp)}ms old)`);
      // Use the fetch method with a pre-resolved callback to return cached data without making a new request
      return await apiCache.fetch('user-profile', async () => {
        // This callback won't run due to in-memory cache, but needs to be provided
        return null;
      }, {
        ttl: MEMORY_CACHE_TTL,
        forceFresh: false
      });
    }
    
    // CRITICAL: If forceFresh is true, always fetch from the server regardless of route
    // This ensures login and other important auth operations work correctly
    if (forceFresh) {
      console.log('[CacheableBackend] Force fresh session requested - bypassing route checks');
      
      try {
        // Mark that a request is in progress to prevent duplicates
        sessionRequestInProgress = true;
        
        // Create a shared promise that can be reused by parallel requests
        sessionPromiseCache = $fetch('/api/custom-auth/me', {
          method: 'GET',
          credentials: 'include'
        }).catch((error) => {
          // Handle 401 unauthorized responses by clearing auth cookies
          if ((error as any)?.status === 401 || (error as any)?.statusCode === 401) {
            console.log('[CacheableBackend] 401 Unauthorized detected - clearing auth cookies');
            
            // Clear authentication cookies
            if (typeof window !== 'undefined') {
              const cookiesToClear = ['SITEACTIVEID', 'SITEACTIVEID.sig'];
              
              cookiesToClear.forEach(cookieName => {
                // Clear cookie with various path combinations to ensure complete removal
                document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
                document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${window.location.hostname};`;
                // Also try with leading dot for domain
                if (window.location.hostname.includes('.')) {
                  const domain = '.' + window.location.hostname.split('.').slice(-2).join('.');
                  document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${domain};`;
                }
              });
              
              console.log('[CacheableBackend] Cleared authentication cookies due to 401');
            }
          }
          
          // Re-throw the error to maintain existing error handling
          throw error;
        });
        
        // Wait for the result
        const response = await sessionPromiseCache;
        
        // Update cache timestamp
        lastSessionTimestamp = Date.now();
        
        // Store sanitized session data in sessionStorage and cookie for WebView fallback
        if (response && typeof sessionStorage !== 'undefined') {
          try {
            const sanitizedResponse = sanitizeResponse(response);
            console.log('🔒 [CacheableBackend] Storing sanitized session data in sessionStorage');
            sessionStorage.setItem('auth_session_data', JSON.stringify(sanitizedResponse));
            sessionStorage.setItem('auth_session_last_refreshed', new Date().toISOString());

            // For WebView fallback, store in cookie (with additional PII protection)
            if (typeof document !== 'undefined') {
              const safeSessionData = JSON.stringify(sanitizeCookie(response));
              console.log('🔒 [CacheableBackend] Storing PII-sanitized session data in cookie for WebView fallback');
              const expiryDate = new Date(Date.now() + 24 * 60 * 60 * 1000).toUTCString(); // 24 hours
              document.cookie = `auth_session_data=${encodeURIComponent(safeSessionData)}; expires=${expiryDate}; path=/`;
            }
          } catch (e: unknown) {
            console.warn('[CacheableBackend] Failed to store sanitized session data', 
              e instanceof Error ? e.message : 'Unknown error');
          }
        }
        
        // Also cache the response
        if (response) {
          // Update the cache using refreshEndpoint with an already-resolved value
          apiCache.refreshEndpoint('user-profile', async () => {
            return response;
          }, CACHE_TIMES.USER_PROFILE);
        }
        
        return response;
      } finally {
        // Always clear the in-progress flag
        sessionRequestInProgress = false;
        // Clear the promise cache after a short delay
        setTimeout(() => {
          sessionPromiseCache = null;
        }, 100);
      }
    }
    
    // For normal (non-forced) requests, check if we're on a protected route
    if (typeof window !== 'undefined') {
      const currentPath = window.location.pathname;
      
      // Check for SITEACTIVEID cookie - if present, always make the session call
      // This ensures we properly recognize logged-in state even on public routes
      const hasSiteActiveIdCookie = document.cookie.split(';').some(c => 
        c.trim().startsWith('SITEACTIVEID=')
      );
      
      if (hasSiteActiveIdCookie) {
        console.log('[CacheableBackend] SITEACTIVEID cookie found - making session call');
      } else {
        // Special handling for /account - login page itself is not protected
        // but all subpaths like /account/dashboard are protected
        const isAccountPage = currentPath === '/account'; // The login page itself
        const isAccountSubpath = currentPath.startsWith('/account/'); // Any subpath under account
        
        // Other fully protected routes (including all subpaths)
        const otherProtectedRoutes = [
          '/sim-activation',
          '/checkout'
        ];
        
        // Check if current path is a protected route
        const requiresAuth = isAccountSubpath || otherProtectedRoutes.some(route => 
          currentPath === route || currentPath.startsWith(`${route}/`)
        );
        
        // Also check if public_route_bypass flag is set in sessionStorage
        const hasPublicBypass = sessionStorage.getItem('public_route_bypass') === 'true';
        
        if (!requiresAuth || hasPublicBypass) {
          console.log(`[CacheableBackend] On public route '${currentPath}' - skipping session API call`);
          
          // Return a dummy response for public routes
          return {
            success: false,
            isPublicRoute: true,
            authRequired: false,
            authenticated: false
          };
        }
      }
    }
    
    // Always use the Nuxt proxy endpoint to avoid direct backend calls and centralise error handling
    // This prevents 404s if the backend path is missing and ensures proper fallback logic
    if (isServerSide.value) {
      // In SSR mode, still use the proxy endpoint
      try {
        const response = await $fetch('/api/auth/session', {
          method: 'GET',
          credentials: 'include'
        });
        return response;
      } catch (error) {
        // Handle 401 unauthorized responses by clearing auth cookies
        if ((error as any)?.status === 401 || (error as any)?.statusCode === 401) {
          console.log('[CacheableBackend] 401 Unauthorized detected in SSR - clearing auth cookies');
          
          // Clear authentication cookies (SSR-safe check)
          if (typeof window !== 'undefined') {
            const cookiesToClear = ['SITEACTIVEID', 'SITEACTIVEID.sig'];
            
            cookiesToClear.forEach(cookieName => {
              // Clear cookie with various path combinations to ensure complete removal
              document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
              document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${window.location.hostname};`;
              // Also try with leading dot for domain
              if (window.location.hostname.includes('.')) {
                const domain = '.' + window.location.hostname.split('.').slice(-2).join('.');
                document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${domain};`;
              }
            });
            
            console.log('[CacheableBackend] Cleared authentication cookies due to 401');
          }
        }
        
        // Re-throw the error to maintain existing error handling
        throw error;
      }
    }
    
    return apiCache.fetch('user-profile', async () => {
      // Always use the Nuxt proxy endpoint for session/profile fetches
      try {
        const response = await $fetch('/api/auth/session', {
          method: 'GET',
          credentials: 'include'
        });
        return response;
      } catch (error) {
        // Handle 401 unauthorized responses by clearing auth cookies
        if ((error as any)?.status === 401 || (error as any)?.statusCode === 401) {
          console.log('[CacheableBackend] 401 Unauthorized detected - clearing auth cookies');
          
          // Clear authentication cookies
          if (typeof window !== 'undefined') {
            const cookiesToClear = ['SITEACTIVEID', 'SITEACTIVEID.sig'];
            
            cookiesToClear.forEach(cookieName => {
              // Clear cookie with various path combinations to ensure complete removal
              document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
              document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${window.location.hostname};`;
              // Also try with leading dot for domain
              if (window.location.hostname.includes('.')) {
                const domain = '.' + window.location.hostname.split('.').slice(-2).join('.');
                document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${domain};`;
              }
            });
            
            console.log('[CacheableBackend] Cleared authentication cookies due to 401');
          }
        }
        
        // Re-throw the error to maintain existing error handling
        throw error;
      }
    }, {
      ttl: CACHE_TIMES.USER_PROFILE,
      forceFresh
    });
  };
  
  // Products - wrapped with caching
  const getProducts = async (forceFresh = false) => {
    // Skip caching in SSR mode
    if (isServerSide.value) {
      return await integrateService.getProducts();
    }
    
    return apiCache.fetch('products', async () => {
      return await integrateService.getProducts();
    }, {
      ttl: CACHE_TIMES.PRODUCTS,
      forceFresh
    });
  };
  
  // Network Balance - wrapped with caching
  const getNetworkBalance = async (customerId: string, forceFresh = false) => {
    // Add global debugging flag to help track when this is called
    if (typeof window !== 'undefined') {
      window.__networkBalanceLastCalled = {
        timestamp: Date.now(),
        customerId
      };
      console.warn('[ApiCache TRACE] getNetworkBalance called for ID:', customerId);
    }
    
    // Skip caching in SSR mode
    if (isServerSide.value) {
      return await mobilePlanService.networkBalance(customerId);
    }

    // Security: Error handling without logging for security (2025-05-05)
    
    const cacheKey = `api_cache_integration/network-balance/${customerId}`;
    
    // Check for existing cache entry directly
    if (!forceFresh && typeof localStorage !== 'undefined') {
      try {
        const cacheData = JSON.parse(localStorage.getItem('go_api_cache') || '{}');
        
        // If we have a valid cache entry for this endpoint
        if (cacheData[cacheKey]) {
          const entry = cacheData[cacheKey];
          const isValid = (Date.now() - entry.timestamp) < CACHE_TIMES.NETWORK_BALANCE;
          
          if (isValid) {
            // Security: Error handling without logging for security (2025-05-05)
            
            // Update access count
            entry.accessCount = (entry.accessCount || 0) + 1;
            localStorage.setItem('go_api_cache', JSON.stringify(cacheData));
            
            // Set window marker for monitoring
            if (typeof window !== 'undefined') {
              window.__lastApiCacheHit = {
                endpoint: `integration/network-balance`,
                timestamp: Date.now(),
                accessCount: entry.accessCount
              };
            }
            
            return entry.data;
          }
        }
      } catch (e) {
        console.error('[ApiCache DEBUG] Error reading cache:', e);
      }
    }
    
    // If we get here, we need to fetch fresh data
    // Security: Error handling without logging for security (2025-05-05)
    
    try {
      // Call the actual service method
      const freshData = await mobilePlanService.networkBalance(customerId);
      
      // Save to cache directly
      if (typeof localStorage !== 'undefined' && typeof window !== 'undefined') {
        try {
          // Get current cache and add our entry
          let cacheData;
          try {
            cacheData = JSON.parse(localStorage.getItem('go_api_cache') || '{}');
          } catch (parseError) {
            console.error('[ApiCache DEBUG] Error parsing cache, creating new cache object');
            cacheData = {};
          }
          
          // Create our cache entry
          const cacheEntry = {
            data: freshData,
            timestamp: Date.now(),
            accessCount: 1,
            endpoint: `integration/network-balance/${customerId}`,
            ttl: CACHE_TIMES.NETWORK_BALANCE
          };
          
          // Add to cache object
          cacheData[cacheKey] = cacheEntry;
          
          // Specifically check for NaN, null, or undefined in the data to prevent JSON issues
          const safeStringify = (obj: Record<string, any>) => {
            return JSON.stringify(obj, (key, value) => {
              if (typeof value === 'number' && isNaN(value)) return 'NaN';
              return value;
            });
          };
          
          // Save to localStorage with error handling
          try {
            const cacheString = safeStringify(cacheData);
            localStorage.setItem('go_api_cache', cacheString);
            // Security: Error handling without logging for security (2025-05-05)
            
            // Add a direct window property to help with debugging
            window.__goCacheLastSaved = {
              key: cacheKey,
              timestamp: Date.now()
            };
          } catch (storageError) {
            console.error('[ApiCache DEBUG] Failed to save to localStorage:', storageError);
            
            // Try again with just this entry in case the overall cache is too large
            try {
              const singleEntryCache: Record<string, any> = {};
              singleEntryCache[cacheKey] = cacheEntry;
              localStorage.setItem('go_api_cache', JSON.stringify(singleEntryCache));
              // Security: Error handling without logging for security (2025-05-05)
            } catch (fallbackError) {
              console.error('[ApiCache DEBUG] Even fallback cache save failed:', fallbackError);
            }
          }
          
          // Verify it was saved
          setTimeout(() => {
            try {
              const verifyData = JSON.parse(localStorage.getItem('go_api_cache') || '{}');
              // Security: Error handling without logging for security (2025-05-05));
              const hasEntry = cacheKey in verifyData;
              // Security: Error handling without logging for security (2025-05-05)
              
              if (!hasEntry) {
                console.error('[ApiCache DEBUG] Cache verification failed - entry not found!');
              }
            } catch (e) {
              console.error('[ApiCache DEBUG] Error verifying cache:', e);
            }
          }, 100);
        } catch (e) {
          console.error('[ApiCache DEBUG] Error in overall cache save process:', e);
        }
      }
      
      return freshData;
    } catch (error) {
      console.error(`[ApiCache DEBUG] Error in getNetworkBalance:`, error);
      throw error;
    }
  };
  
  // Current Services - wrapped with caching
  const getCurrentServices = async (forceFresh = false) => {
    console.log("[CacheService Diagnostics] getCurrentServices called with forceFresh =", forceFresh);
    console.log("[CacheService Diagnostics] isServerSide =", isServerSide.value);
    
    // Skip caching in SSR mode
    if (isServerSide.value) {
      console.log("[CacheService Diagnostics] Server-side execution, bypassing cache");
      return await mobilePlanService.currentServices();
    }
    
    // Check if cache exists before fetch
    if (typeof localStorage !== "undefined") {
      try {
        const cacheData = JSON.parse(localStorage.getItem("go_api_cache") || "{}");
        const cacheKey = `api_cache_current-services`;
        console.log("[CacheService Diagnostics] Cache check:", {
          cacheExists: !!cacheData[cacheKey],
          cacheAge: cacheData[cacheKey] ? (Date.now() - cacheData[cacheKey].timestamp) : "N/A",
          cacheTTL: CACHE_TIMES.CURRENT_SERVICES,
          willForceFresh: forceFresh
        });
      } catch (e) {
        console.error("[CacheService Diagnostics] Error checking cache:", e);
      }
    }
    
    console.log("[CacheService Diagnostics] Calling apiCache.fetch for current-services");
    const startTime = Date.now();
    
    try {
      const result = await apiCache.fetch(`current-services`, async () => {
        // currentServices doesn't take any parameters
        console.log("[CacheService Diagnostics] Cache miss or force refresh, calling actual service");
        const serviceResult = await mobilePlanService.currentServices();
        console.log("[CacheService Diagnostics] Service call completed, result structure:", {
          hasResult: !!serviceResult,
          resultType: typeof serviceResult,
          hasBody: serviceResult && !!serviceResult.body,
          isArray: Array.isArray(serviceResult),
          keys: serviceResult ? Object.keys(serviceResult) : []
        });
        return serviceResult;
      }, {
        ttl: CACHE_TIMES.CURRENT_SERVICES,
        forceFresh
      });
      
      console.log(`[CacheService Diagnostics] getCurrentServices completed in ${Date.now() - startTime}ms`);
      return result;
    } catch (error) {
      console.error(`[CacheService Diagnostics] getCurrentServices failed after ${Date.now() - startTime}ms:`, error);
      throw error;
    }
  };
  
  // Recharges - wrapped with caching
  const getRecharges = async (customerId: string, forceFresh = false) => {
    // Skip caching in SSR mode
    if (isServerSide.value) {
      return await $fetch(`/api/integration/recharges/${customerId}`, {
        method: 'GET',
        credentials: 'include'
      });
    }
    
    return apiCache.fetch(`recharges-${customerId}`, async () => {
      return await $fetch(`/api/integration/recharges/${customerId}`, {
        method: 'GET',
        credentials: 'include'
      });
    }, {
      ttl: CACHE_TIMES.RECHARGES,
      forceFresh
    });
  };
  
  // Force refresh a specific endpoint
  const refreshEndpoint = async (endpoint: string, params: any = {}) => {
    // Skip refresh operations in SSR mode
    if (isServerSide.value) {
      // Security: Error handling without logging for security (2025-05-05)
      return null;
    }
    switch (endpoint) {
      case 'products':
        return apiCache.refreshEndpoint('products', async () => {
          return await integrateService.getProducts();
        }, CACHE_TIMES.PRODUCTS);
        
      case 'network-balance':
        if (!params.customerId) throw new Error('Customer ID is required to refresh network-balance');
        // Use our custom implementation to bypass the standard apiCache method
        if (typeof localStorage !== 'undefined') {
          try {
            // Remove the existing cache entry
            const cacheKey = `api_cache_integration/network-balance/${params.customerId}`;
            const cacheData = JSON.parse(localStorage.getItem('go_api_cache') || '{}');
            delete cacheData[cacheKey];
            localStorage.setItem('go_api_cache', JSON.stringify(cacheData));
            // Security: Error handling without logging for security (2025-05-05)
          } catch (e) {
            console.error('[ApiCache DEBUG] Error clearing cache:', e);
          }
        }
        // Get fresh data by calling getNetworkBalance with forceFresh=true
        return getNetworkBalance(params.customerId, true);
        
      case 'current-services':
        return apiCache.refreshEndpoint(`current-services`, async () => {
          // currentServices doesn't take any parameters
          return await mobilePlanService.currentServices();
        }, CACHE_TIMES.CURRENT_SERVICES);
        
      case 'user-profile':
        // Added May 2025: Mark that a request is in progress to prevent duplicates
        sessionRequestInProgress = true;
        
        try {
          // Create a shared promise that can be reused by parallel requests
          sessionPromiseCache = $fetch('/api/custom-auth/me', {
            method: 'GET',
            credentials: 'include'
          }).catch((error) => {
            // Handle 401 unauthorized responses by clearing auth cookies
            if ((error as any)?.status === 401 || (error as any)?.statusCode === 401) {
              console.log('[CacheableBackend] 401 Unauthorized detected - clearing auth cookies');
              
              // Clear authentication cookies
              if (typeof window !== 'undefined') {
                const cookiesToClear = ['SITEACTIVEID', 'SITEACTIVEID.sig'];
                
                cookiesToClear.forEach(cookieName => {
                  // Clear cookie with various path combinations to ensure complete removal
                  document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
                  document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${window.location.hostname};`;
                  // Also try with leading dot for domain
                  if (window.location.hostname.includes('.')) {
                    const domain = '.' + window.location.hostname.split('.').slice(-2).join('.');
                    document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${domain};`;
                  }
                });
                
                console.log('[CacheableBackend] Cleared authentication cookies due to 401');
              }
            }
            
            // Re-throw the error to maintain existing error handling
            throw error;
          });
          
          // Wait for the result and cache it
          const response = await sessionPromiseCache;
          lastSessionTimestamp = Date.now();
          
          // Store sanitized session data in sessionStorage and cookie for WebView fallback
          if (response && typeof sessionStorage !== 'undefined') {
            try {
              const sanitizedResponse = sanitizeResponse(response);
              console.log('🔒 [CacheableBackend] Storing sanitized session data in sessionStorage (refreshEndpoint)');
              sessionStorage.setItem('auth_session_data', JSON.stringify(sanitizedResponse));
              sessionStorage.setItem('auth_session_last_refreshed', new Date().toISOString());

              // For WebView fallback, store in cookie (with additional PII protection)
              if (typeof document !== 'undefined') {
                const safeSessionData = JSON.stringify(sanitizeCookie(response));
                console.log('🔒 [CacheableBackend] Storing PII-sanitized session data in cookie for WebView fallback (refreshEndpoint)');
                const expiryDate = new Date(Date.now() + 24 * 60 * 60 * 1000).toUTCString(); // 24 hours
                document.cookie = `auth_session_data=${encodeURIComponent(safeSessionData)}; expires=${expiryDate}; path=/`;
              }
            } catch (e: unknown) {
              console.warn('[CacheableBackend] Failed to store sanitized session data (refreshEndpoint)', 
                e instanceof Error ? e.message : 'Unknown error');
            }
          }
          
          return apiCache.refreshEndpoint('user-profile', async () => {
            return response;
          }, CACHE_TIMES.USER_PROFILE);
        } finally {
          // Always clear the in-progress flag
          sessionRequestInProgress = false;
          // Clear the promise cache after a short delay
          setTimeout(() => {
            sessionPromiseCache = null;
          }, 100);
        }
        
      case 'recharges':
        if (!params.customerId) throw new Error('Customer ID is required to refresh recharges');
        return apiCache.refreshEndpoint(`recharges-${params.customerId}`, async () => {
          return await $fetch(`/api/integration/recharges/${params.customerId}`, {
            method: 'GET',
            credentials: 'include'
          });
        }, CACHE_TIMES.RECHARGES);
        
      default:
        throw new Error(`Unknown endpoint: ${endpoint}`);
    }
  };
  
  // Cache control functions
  const invalidateCache = (endpoint: string) => {
    if (isServerSide.value) return; // Skip in SSR mode
    apiCache.invalidate(endpoint);
  };
  
  const invalidateAllCache = () => {
    if (isServerSide.value) return; // Skip in SSR mode
    apiCache.invalidateAll();
  };
  
  // Return everything from original services plus our enhanced cached methods
  return {
    // Original methods from all services
    ...authService,
    ...usageService,
    ...integrateService,
    ...simService,
    ...voucherService,
    ...mobilePlanService,
    
    // Enhanced cached methods (these will override the originals)
    getProducts,
    getNetworkBalance,
    getCurrentServices,
    getRecharges,
    getSession,
    
    // Cache control methods
    refreshEndpoint,
    invalidateCache,
    invalidateAllCache
  };
};
